import '@babylonjs/core/Meshes/thinInstanceMesh';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import {
  ATTACKER,
  DEFAULT_ARENA_SKILL,
  TileMap,
  arenaTiming,
  createBattle,
  createBattleContext,
  createServedBattle,
  applyDuelLevelStats,
  fightsAs,
  nearestDuelLevel,
  cellBrightness,
  fighterOf,
  luminosityHitPoints,
  opponentOf,
  pieceById,
  type ArenaAiSkill,
  type ArenaDef,
  type BattleEvent,
  type BattleResult,
  type BattleState,
  type DuelLevel,
  type Fighter,
  type FighterId,
  type GameContext,
  type GameState,
  type Roster,
  type TilesetDef,
  type FighterInput,
  type Vec3,
} from '@archon3d/core';
import type { Engine } from '@babylonjs/core/Engines/engine';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Scene } from '@babylonjs/core/scene';
import type { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { WebXRState } from '@babylonjs/core/XR/webXRTypes';
import type { BattleFeed, BattleView } from '../../game/battleFeed';
import { suddenDeathWarning } from '../../game/duelClock';
import { shotFlavour, type ProjectileFlavour } from '../../game/projectileFlavour';
import type { LockstepLink } from '../../game/lockstepLink';
import { createCompositeFighterInput } from '../../input/compositeFighterInput';
import { createDesktopFighterInput } from '../../input/desktopFighterInput';
import type { FighterInputSource } from '../../input/fighterInput';
import { createGamepadFighterInput } from '../../input/gamepadFighterInput';
import { coarsePointer, createTouchFighterInput } from '../../input/touchFighterInput';
import { createXrFighterInput } from '../../input/xrFighterInput';
import { seatPads, type SeatDevice } from '../../game/controls';
import { type ShadowQuality } from '../../ui/settings';
import type { CharacterModelCache } from '../board/characterModels';
import { PALETTES, factionColor, type Palette } from '../board/palette';
import { applyShadowQuality } from '../shadowQuality';
import { BOARD_CENTER } from '../board/constants';
import { createArenaCamera, type ArenaCamera } from './arenaCamera';
import { tallestStanding } from './arenaFit';
import { dressArena } from './arenaDressing';
import { measureArenaFrame, WHOLE_CANVAS, type ArenaFrame } from './arenaFrame';
import { buildArenaMeshes } from './arenaMesh';
import { createArenaHud, type ArenaHud } from './arenaHud';
import { intentToInput } from './fighterIntent';
import { BattleController, FIGHTER_IDS } from './battleController';
import { createArenaMaterial } from './arenaMaterial';
import { FighterView } from './fighterView';
import { INTRO_HOLD_SECONDS, introCount } from './intro';
import { arenaDaylight, type ArenaDaylight } from './arenaDaylight';
import { ambienceFile } from '../../audio/ambience';
import { DuelSparks } from './duelSparks';
import { killPace } from './killPace';
import { ReadyRings, readiness } from './readyRings';
import { SHOT_LOOKS, shotMatrix } from './shotLooks';

export interface BattleSetup {
  game: GameContext;
  /** A game in the `battle` phase. */
  state: GameState;
  /** The sides people at this device fight; empty when only watching. */
  humans: readonly FighterId[];
  /** Which controller drives each of those sides, when the people have one each. */
  devices?: Partial<Record<FighterId, SeatDevice>>;
  humanFriendlyAi?: boolean;
  seed: number;
  /** A menu duel: the outcome is shown but changes nothing. */
  practice: boolean;
  /** An online duel: the simulation follows the server's bundles. */
  lockstep?: LockstepLink;
  /** The arena AI's skill per side it drives, where a seat has its own setting. */
  skills?: Partial<Record<FighterId, ArenaAiSkill>>;
  /**
   * An online duel: the level the server scaled each of its AI sides to. The copy stepped here
   * has to start exactly as the server's did, so these alone decide the stats.
   */
  levels?: Partial<Record<FighterId, DuelLevel>>;
  onFinished(result: BattleResult): void;
}

export interface ArenaStageDeps {
  scene: Scene;
  engine: Engine;
  models: CharacterModelCache;
  roster: Roster;
  arena: ArenaDef;
  /** The tileset the arena's legend resolves against. */
  tileset: TilesetDef;
  feed: BattleFeed;
  xr?: WebXRDefaultExperience;
  /**
   * The room the table stands in (its floor, the table, the temple). In the headset it stays round
   * the arena standing on the table, lit by the board's lights: the arena's own leave it alone.
   */
  room?: readonly AbstractMesh[];
  /** How well AI fighters play; `DEFAULT_ARENA_SKILL` unless given. */
  aiSkill?: ArenaAiSkill;
  /** Fades the whole picture to black (0) or back to normal (1). */
  fade(target: number, seconds: number): Promise<void>;
  /** The scene switches its own world (board, lights, cameras) in and out around the fight. */
  onModeChange(fighting: boolean, setup: BattleSetup): void;
}

type ArenaEventBody =
  | { type: 'phase'; phase: 'fighting' | 'over' }
  /**
   * The arena's ambience as the duel comes in, by its theme and its square's light (audit step
   * 26b), `null` for none; and `leaving` as the arena gives the picture back to the table, whose
   * own loop then plays again.
   */
  | { type: 'ambience'; file: string | null; leaving?: boolean }
  /** The count before the fight: 3, 2 and 1. */
  | { type: 'count'; n: number }
  | { type: 'battle'; event: BattleEvent }
  | { type: 'step'; fighter: FighterId }
  | { type: 'clock' };

/**
 * What the stage reports while a duel runs, for sound: the simulation's own events, footfalls
 * and landings, every whole second, and the fight starting and being decided. `state` is the
 * live simulation – read it, do not keep it.
 */
export type ArenaStageEvent = ArenaEventBody & {
  state: BattleState;
  /** The sides people at this device fight; empty when only watching. */
  humans: readonly FighterId[];
  /**
   * Where a point of the arena stands in the world, for placing its sound: at true size before
   * the screen's camera, or a model on the table in the headset (client-audio-engine:bugs#1).
   */
  toWorld(at: Vec3): Vec3;
};

export interface ArenaStage {
  readonly active: boolean;
  /** Whether the running duel stands on the table as a model (the headset), not at true size. */
  readonly onTable: boolean;
  /** The middle of the arena's floor in the world, where the camera looks: sounds are heard from here. */
  centre(): Vec3;
  /** The running duel's light, from its square's on the board: noon between duels. */
  readonly daylight: ArenaDaylight;
  /** The arena's theme: which sky it is fought under and which ambience it plays. */
  readonly theme: ArenaDef['theme'];
  /** Reports the events of the running duel; returns the unsubscribe. */
  onEvent(listener: (event: ArenaStageEvent) => void): () => void;
  /** The skill AI fighters get in the next duel. */
  readonly aiSkill: ArenaAiSkill;
  start(setup: BattleSetup): void;
  /** Changes how well AI fighters play from the next duel on; the running one keeps its skill. */
  setAiSkill(skill: ArenaAiSkill): void;
  /** The local player gives up the running fight. */
  /** A person gives the duel up: the first person's side unless a side is named. */
  forfeit(side?: FighterId): void;
  /**
   * Ends the running duel at once, with no outcome and no result: the game it was fought for has
   * left the table (client-board:quality#15).
   */
  abort(): void;
  /** The outcome has been read: the picture goes back to the board now, not when its time is up. */
  continueOutcome(): void;
  /** Where on the canvas the arena is framed, as shares of it from the top left; for tests. */
  frame(): ArenaFrame;
  /**
   * Moves a staged duel's clock on to `seconds`, for tests of its late minutes: the warning of
   * sudden death, the ring, the verdict of the clock. A duel on the board or online keeps its time.
   */
  skipClockTo(seconds: number): void;
  /** Shadows on the chunks; `off` for the Quest, which cannot afford the shadow pass. */
  setShadows(quality: ShadowQuality): void;
  /** The faction colours of the HUD and the stand-in capsules follow the board's palette. */
  setPalette(palette: Palette): void;
  dispose(): void;
}

const FADE_SECONDS = 0.45;
/** A fighter slowed by its piece's Curse (D-31): the panels say so beside its name. */
const isCursed = (f: Fighter) => (f.pace ?? 1) < 1;
/**
 * How long the outcome plays out before the picture returns to the board, or Continue takes it
 * back sooner: at the table there is time to read how the fight went (x-player-journey#8); online
 * the room's game goes on meanwhile, so the outcome keeps to its old two seconds.
 */
const OUTCOME_SECONDS = 4;
const SERVED_OUTCOME_SECONDS = 2;
const HUD_HZ = 10;
const HIT_FLASH_SECONDS = 0.35;
/** A fighter still counts as burning this long after the ground's fire last bit it. */
const BURN_SHOWN_MS = 300;
/** Metres of ground between two footfalls of a human-sized fighter at a run. */
const STRIDE = 1.3;
/** The safe ring stands clear of the tallest thing an arena can put on a tile. */
const RING_HEIGHT = 10;
/**
 * In the headset the arena is a model on the same walnut table as the board (D-16), spanning
 * about as much of it as a nine-square board does. There is no locomotion anywhere in the game
 * any more: the player is already sitting at the table this is standing on.
 */
const DIORAMA_SPAN = 0.9;
/**
 * On the table the arena stands on a base this tall, in metres, like a model. The ground is a skin
 * with no underside or rim, and a stream's water lies below it: set down on the walnut, the two
 * would fight for the same pixels and the table would cover the water.
 */
const PLINTH = 0.02;
/**
 * The headset's HUD plaque floats this high over the table and this far beyond the diorama's far
 * edge, clear of the fight, and is as wide as the far tablet the turn sign hangs on.
 */
const HUD_ABOVE = 0.22;
const HUD_BEYOND = 0.24;
const HUD_WIDTH = 0.5;

interface RunningBattle {
  setup: BattleSetup;
  controller: BattleController;
  views: Partial<Record<FighterId, FighterView>>;
  /** A device per side a person fights. */
  inputs: Partial<Record<FighterId, FighterInputSource>>;
  phase: 'intro' | 'fighting' | 'over' | 'leaving';
  /** The arena has taken the picture over (`switchIn`), so leaving it means switching out. */
  shown: boolean;
  /** When the hold after the fade in ends (`performance.now()` milliseconds); 0 while fading. */
  fightAt: number;
  /** What the count shows now, as last published. */
  countdown: number | null;
  /** Hit points the square's light adds to each fighter, for the intro. */
  light: Partial<Record<FighterId, number>>;
  overAt: number;
  /** When the blow that ended the fight fell (`performance.now()`), for the slow motion; 0 before. */
  killedAt: number;
  /** The clock ended the fight: the fitter fighter won (the core's `timeout` event). */
  timedOut: boolean;
  /** Until when each fighter counts as burning (`performance.now()`): the ground's fire bites every step. */
  burningUntil: Partial<Record<FighterId, number>>;
  /** What each side shoots, for its shots' look. */
  flavours: Partial<Record<FighterId, ProjectileFlavour>>;
  /** Each side's colour on the board, for its ready ring. */
  colors: Partial<Record<FighterId, Color3>>;
  hudTimer: number;
  hitFlash: number;
  /** Ground covered since each fighter's last footfall. */
  stride: Record<FighterId, number>;
  /** The last whole second reported. */
  second: number;
}

/**
 * The duel world inside the shared scene: chunk meshes, day lights, fighters, projectiles, the
 * sudden-death ring and the HUD. It is switched on for a fight and off again afterwards, so the
 * XR session (which belongs to the scene) simply carries over from the board.
 */
export function createArenaStage(deps: ArenaStageDeps): ArenaStage {
  const { scene, engine, models, roster, arena, tileset, feed, xr } = deps;
  const root = new TransformNode('arena', scene);
  root.setEnabled(false);
  const tileMaterial = createArenaMaterial(scene);
  const map = new TileMap(arena, tileset);
  const chunks = buildArenaMeshes(scene, map, tileMaterial, root);
  // The base's four sides, open above (the ground covers it) and below (it stands on the table).
  const plinth = MeshBuilder.CreateCylinder(
    'arenaPlinth',
    { height: 1, diameter: Math.SQRT2, tessellation: 4, cap: Mesh.NO_CAP },
    scene,
  );
  plinth.rotation.y = Math.PI / 4;
  plinth.bakeCurrentTransformIntoVertices();
  plinth.parent = root;
  plinth.isPickable = false;
  const plinthMaterial = new StandardMaterial('arenaPlinth', scene);
  plinthMaterial.diffuseColor = new Color3(0.17, 0.13, 0.1);
  plinthMaterial.specularColor = new Color3(0.05, 0.05, 0.05);
  plinth.material = plinthMaterial;
  plinth.setEnabled(false);

  const skyLight = new HemisphericLight('arenaSky', new Vector3(0, 1, 0), scene);
  skyLight.intensity = 0.9;
  skyLight.diffuse = new Color3(0.95, 0.97, 1);
  skyLight.groundColor = new Color3(0.35, 0.42, 0.3);
  skyLight.setEnabled(false);
  skyLight.excludedMeshes = [...(deps.room ?? [])];
  const sun = new DirectionalLight('arenaSun', new Vector3(-0.4, -1, -0.5).normalize(), scene);
  sun.excludedMeshes = [...(deps.room ?? [])];
  sun.position = new Vector3(arena.width * 0.8, 40, arena.height * 0.9);
  sun.intensity = 1.3;
  sun.diffuse = new Color3(1, 0.96, 0.9);
  sun.shadowMinZ = 1;
  sun.shadowMaxZ = 120;
  sun.setEnabled(false);
  // What the arena's data describes beyond its blocks (content:quality#2, x-aaa-gap#10): its
  // trees, the surfaces its liquids name, its fog banks and its lamps, whose lights come after
  // the sky's and the sun's.
  const dressing = dressArena(scene, root, arena, map, deps.room);
  const { trees, liquids, fog, lamps } = dressing;
  /** What never moves on the arena's floor, whose world matrices are frozen where it stands. */
  const still = [...chunks, ...dressing.still];
  const shadows = new ShadowGenerator(1024, sun);
  shadows.usePercentageCloserFiltering = true;
  shadows.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
  shadows.bias = 0.002;
  shadows.normalBias = 0.02;
  shadows.setDarkness(0.4);

  const projectiles = MeshBuilder.CreateSphere(
    'projectiles',
    { diameter: 0.36, segments: 8 },
    scene,
  );
  projectiles.parent = root;
  projectiles.isPickable = false;
  const projectileMaterial = new StandardMaterial('projectile', scene);
  projectileMaterial.disableLighting = true;
  // White: each shot's own colour comes with it (`shotLooks.ts`).
  projectileMaterial.emissiveColor = Color3.White();
  projectiles.material = projectileMaterial;
  projectiles.thinInstanceSetBuffer('matrix', new Float32Array(0), 16, false);
  // There from the start, so the first shot does not have to build the shader over again.
  projectiles.thinInstanceSetBuffer('color', new Float32Array(0), 4, false);
  projectiles.isVisible = false;
  // The shots move every frame, so their box would always be stale; the arena is always in view.
  projectiles.alwaysSelectAsActiveMesh = true;
  const shotAt = new Matrix();
  /** Room for this many shots in the buffers below, grown when a volley needs more. */
  let shotRoom = 0;
  let shotMatrices = new Float32Array(0);
  let shotColors = new Float32Array(0);
  const sparks = new DuelSparks(scene, root);
  const readyRings = new ReadyRings(scene, root);

  const ring = MeshBuilder.CreateCylinder(
    'safeRing',
    {
      height: RING_HEIGHT,
      diameter: 2,
      tessellation: 48,
      cap: Mesh.NO_CAP,
      sideOrientation: Mesh.DOUBLESIDE,
    },
    scene,
  );
  ring.parent = root;
  ring.isPickable = false;
  ring.position.set(arena.width / 2, RING_HEIGHT / 2, arena.height / 2);
  const ringMaterial = new StandardMaterial('safeRing', scene);
  ringMaterial.disableLighting = true;
  ringMaterial.emissiveColor = new Color3(1, 0.45, 0.1);
  ringMaterial.alpha = 0.3;
  ringMaterial.backFaceCulling = false;
  ring.material = ringMaterial;
  ring.setEnabled(false);

  // The one camera a duel has: fixed, three-quarter, and the same for a player and a spectator.
  // Fitted to the floor and to the tops of its tallest walls, so the far wall stays in the frame.
  const arenaCam: ArenaCamera = createArenaCamera(scene, arena, tallestStanding(map));
  /** Metres of table per arena metre when the arena is a model on it rather than a place. */
  const dioramaScale = DIORAMA_SPAN / Math.max(arena.width, arena.height);
  const hud: ArenaHud = createArenaHud(scene);
  let palette: Palette = PALETTES.classic;

  /** The scale the arena was last laid out at: on the table (`true`) or at true size. */
  let placedAsDiorama: boolean | null = null;
  /**
   * Stands the arena on the table for the headset, or lays it out at true size for the screen.
   * In VR the player is seated at the table already, so the arena comes to them.
   */
  const placeArena = (asDiorama: boolean) => {
    if (asDiorama === placedAsDiorama) return;
    placedAsDiorama = asDiorama;
    plinth.setEnabled(asDiorama);
    if (asDiorama) {
      root.scaling.setAll(dioramaScale);
      root.position.set(
        BOARD_CENTER.x - (arena.width / 2) * dioramaScale,
        BOARD_CENTER.y + PLINTH,
        BOARD_CENTER.z - (arena.height / 2) * dioramaScale,
      );
      const tall = PLINTH / dioramaScale;
      plinth.scaling.set(arena.width, tall, arena.height);
      plinth.position.set(arena.width / 2, -tall / 2, arena.height / 2);
    } else {
      root.scaling.setAll(1);
      root.position.setAll(0);
    }
    // The floor never moves at either scale, so its chunks, trees and liquids keep frozen world
    // matrices: frozen again under the root whenever the arena goes from the table to true size
    // or back.
    root.computeWorldMatrix(true);
    for (const mesh of still) mesh.freezeWorldMatrix();
  };

  /**
   * Where on the arena floor a screen position points. The cursor is how a player aims now, so
   * this is the whole of desktop aiming: a ray through the pixel, met with the ground plane.
   */
  const groundAt = (clientX: number, clientY: number) => {
    const ray = scene.createPickingRay(clientX, clientY, null, arenaCam.camera);
    // The floor a fighter stands on is y = 0 in arena space; the root may be scaled onto a table.
    const originY = ray.origin.y - root.position.y;
    if (Math.abs(ray.direction.y) < 1e-6 || originY * ray.direction.y >= 0) return null;
    const t = -originY / ray.direction.y;
    return {
      x: (ray.origin.x + ray.direction.x * t - root.position.x) / root.scaling.x,
      z: (ray.origin.z + ray.direction.z * t - root.position.z) / root.scaling.z,
    };
  };

  /**
   * The headset's HUD: a plaque just beyond the diorama's far edge, its face turned to the
   * player's eyes. On a screen the battle panel is the HUD, so the plaque stays down.
   */
  const placeHud = (asDiorama: boolean) => {
    if (!asDiorama) {
      hud.hide();
      return;
    }
    const at = new Vector3(
      root.position.x + (arena.width / 2) * dioramaScale,
      root.position.y + HUD_ABOVE,
      root.position.z - HUD_BEYOND,
    );
    hud.place(at, HUD_WIDTH, scene.activeCamera?.globalPosition ?? null);
  };

  let running: RunningBattle | null = null;
  /**
   * On a screen the battle panel, the floating controls and the touch controls are drawn over the
   * canvas; the camera keeps the arena in the part of it they leave free (`ArenaCamera.setFrame`).
   * The page is measured a few times a second rather than every frame: layout reads are not free,
   * and nothing moves faster.
   */
  let frameTimer: ReturnType<typeof setInterval> | null = null;
  const refreshFrame = () => {
    const canvas = engine.getRenderingCanvas();
    if (!canvas || inXr()) {
      arenaCam.setFrame(WHOLE_CANVAS);
      return;
    }
    arenaCam.setFrame(measureArenaFrame(canvas));
  };
  const watchFrame = () => {
    if (frameTimer !== null) return;
    refreshFrame();
    frameTimer = setInterval(refreshFrame, 250);
  };
  const unwatchFrame = () => {
    if (frameTimer !== null) clearInterval(frameTimer);
    frameTimer = null;
    arenaCam.setFrame(WHOLE_CANVAS);
  };
  let inputInXr = false;
  let frameObserver: Observer<Scene> | null = null;
  let disposed = false;
  const listeners = new Set<(event: ArenaStageEvent) => void>();
  const inWorld = new Vector3();
  const toWorld = (at: Vec3): Vec3 => {
    Vector3.TransformCoordinatesFromFloatsToRef(at.x, at.y, at.z, root.getWorldMatrix(), inWorld);
    return { x: inWorld.x, y: inWorld.y, z: inWorld.z };
  };
  const emit = (battle: RunningBattle, body: ArenaEventBody) => {
    if (listeners.size === 0) return;
    const event: ArenaStageEvent = {
      ...body,
      state: battle.controller.state,
      humans: battle.controller.humans,
      toWorld,
    };
    for (const listener of listeners) listener(event);
  };
  let aiSkill: ArenaAiSkill = { ...(deps.aiSkill ?? DEFAULT_ARENA_SKILL) };
  const { suddenDeathAt } = arenaTiming(arena);
  const isBurning = (battle: RunningBattle, id: FighterId) =>
    (battle.burningUntil[id] ?? 0) > performance.now();

  const inXr = () => xr?.baseExperience.state === WebXRState.IN_XR;

  /**
   * What drives a fighter on a screen, as opposed to in the headset.
   *
   * A finger gets the on-screen pad *instead of* mouse and keys, not as well: a phone has neither,
   * and the synthetic mouse events a tap produces would hand the cursor the fighter's facing and
   * fight the stick for it. A pad is always read alongside whichever of the two it is, because a
   * player at a desk should be able to pick a controller up mid-duel without saying so first.
   * When the people at the computer have a controller each, a side gets its own one only: the
   * mouse and keys, or the one pad its person chose.
   */
  const createFlatFighterInput = (
    canvas: HTMLCanvasElement,
    device?: SeatDevice,
    others: readonly (SeatDevice | undefined)[] = [],
  ): FighterInputSource => {
    const pointer = () =>
      coarsePointer()
        ? createTouchFighterInput(canvas.parentElement ?? document.body)
        : createDesktopFighterInput(canvas, groundAt);
    if (device === undefined)
      return createCompositeFighterInput([pointer(), createGamepadFighterInput()]);
    if (device === 'mouse') return pointer();
    return createGamepadFighterInput(() =>
      seatPads(
        typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function'
          ? navigator.getGamepads()
          : [],
        device,
        others,
      ),
    );
  };
  /** Every person's device for a screen, one per side they fight. */
  const createFlatInputs = (
    battle: RunningBattle,
    canvas: HTMLCanvasElement,
  ): Partial<Record<FighterId, FighterInputSource>> => {
    const inputs: Partial<Record<FighterId, FighterInputSource>> = {};
    const devices = battle.setup.devices;
    for (const side of battle.controller.humans) {
      const others = battle.controller.humans.filter((s) => s !== side).map((s) => devices?.[s]);
      inputs[side] = createFlatFighterInput(canvas, devices?.[side], others);
    }
    return inputs;
  };
  const disposeInputs = (battle: RunningBattle) => {
    for (const input of Object.values(battle.inputs)) input?.dispose();
    battle.inputs = {};
  };
  const sideColor = (setup: BattleSetup, faction: string) => {
    const side = setup.game.board.def.factions.find((f) => f.id === faction);
    return side ? factionColor(palette, side) : '#ffffff';
  };

  /** A person holding the button that gives the duel up, from whichever device it is. */
  const forfeitHoldOf = (battle: RunningBattle) =>
    Object.values(battle.inputs)
      .map((input) => input?.forfeitHold?.() ?? null)
      .find((h) => h !== null) ?? null;
  const missingOf = (battle: RunningBattle): string | null => {
    const side = FIGHTER_IDS.find((id) => battle.inputs[id]?.connected?.() === false);
    if (!side) return null;
    const f = fighterOf(battle.controller.state, side);
    return roster[f.character]?.name ?? f.character;
  };
  const padConnected = () =>
    typeof navigator !== 'undefined' &&
    typeof navigator.getGamepads === 'function' &&
    navigator.getGamepads().some((pad) => pad?.connected);

  const publish = (battle: RunningBattle) => {
    const { state } = battle.controller;
    const status = (id: FighterId) => {
      const f = fighterOf(state, id);
      const light = battle.light[id];
      return {
        name: roster[f.character]?.name ?? f.character,
        faction: f.faction,
        hp: Math.ceil(f.hp),
        maxHp: f.maxHp,
        ...(isCursed(f) ? { cursed: true } : {}),
        ...(light !== undefined ? { light } : {}),
        ...(isBurning(battle, id) ? { burning: true } : {}),
      };
    };
    const result = state.result;
    let outcome: BattleView['outcome'] = null;
    let winner: FighterId | null = null;
    if (result) {
      const human = battle.controller.human;
      winner = FIGHTER_IDS.find((id) => fighterOf(state, id).pieceId === result.winnerId) ?? null;
      outcome =
        result.winnerId === null
          ? 'draw'
          : human === null || battle.controller.humans.length > 1
            ? null
            : result.winnerId === fighterOf(state, human).pieceId
              ? 'won'
              : 'lost';
    }
    feed.publish({
      arenaId: arena.id,
      arenaName: arena.name,
      phase: battle.phase === 'leaving' ? 'over' : battle.phase,
      countdown: battle.countdown,
      human: battle.controller.human,
      humans: [...battle.controller.humans],
      attacker: status('attacker'),
      defender: status('defender'),
      time: state.time,
      suddenDeath: state.suddenDeath,
      suddenDeathAt,
      timedOut: battle.timedOut,
      outcome,
      winner,
      practice: battle.setup.practice,
      forfeitHold: forfeitHoldOf(battle),
      pad: padConnected(),
      // A side whose own controller has gone stands still: its person is told, by name.
      missingController: missingOf(battle),
    });
  };

  const updateHud = (battle: RunningBattle) => {
    const { state, human } = battle.controller;
    const me = human ?? ATTACKER;
    const enemy = opponentOf(state, me);
    const fighter = (id: FighterId) => {
      const f = fighterOf(state, id);
      return {
        name: roster[f.character]?.name ?? f.character,
        ...(isCursed(f) ? { cursed: true } : {}),
        hp: f.hp,
        maxHp: f.maxHp,
        color: sideColor(battle.setup, f.faction),
        ready: readiness(f),
      };
    };
    const result = state.result;
    const winnerName = (id: number) => {
      const f = FIGHTER_IDS.map((side) => fighterOf(state, side)).find((x) => x.pieceId === id);
      return f ? (roster[f.character]?.name ?? f.character) : 'Nobody';
    };
    // A player holding the button that gives the duel up is told to keep holding it.
    const hold = forfeitHoldOf(battle);
    const count = battle.countdown;
    const message = !result
      ? count !== null
        ? count === 0
          ? 'Fight!'
          : `${fighter(me).name} vs ${fighter(enemy).name} · ${count}`
        : hold
          ? `Keep holding ${hold.button} to give up`
          : state.suddenDeath
            ? 'The arena burns from the edges'
            : suddenDeathWarning(state.time, suddenDeathAt, state.suddenDeath)
      : (battle.timedOut ? 'Time up · ' : '') +
        (result.winnerId === null
          ? 'Both fall'
          : human === null || battle.controller.humans.length > 1
            ? `${winnerName(result.winnerId)} wins`
            : result.winnerId === fighterOf(state, me).pieceId
              ? 'Victory'
              : 'Defeated');
    // The plaque's frame reddens when a blow lands on the player's fighter, and throbs while the
    // ground burns it.
    const burn = isBurning(battle, me) ? 0.35 + 0.25 * Math.sin(performance.now() / 80) : 0;
    hud.update({
      me: fighter(me),
      enemy: fighter(enemy),
      time: state.time,
      message,
      hitFlash: Math.max(0, battle.hitFlash / HIT_FLASH_SECONDS, burn),
    });
  };

  /** Footfalls from the ground covered, and every whole second of the clock. */
  const reportMotion = (battle: RunningBattle, dt: number) => {
    const { state } = battle.controller;
    for (const id of state.seats) {
      const f = fighterOf(state, id);
      if (!f.alive) continue;
      const speed = Math.hypot(f.vel.x, f.vel.z);
      if (speed > 0.5) battle.stride[id] = (battle.stride[id] ?? 0) + speed * dt;
      if ((battle.stride[id] ?? 0) >= (STRIDE * f.height) / 1.7) {
        battle.stride[id] = 0;
        emit(battle, { type: 'step', fighter: id });
      }
    }
    const second = Math.floor(state.time);
    if (second > battle.second) {
      battle.second = second;
      emit(battle, { type: 'clock' });
    }
  };

  const frame = () => {
    const battle = running;
    if (!battle) return;
    const dt = engine.getDeltaTime() / 1000;
    const now = performance.now();
    if (battle.phase === 'intro' && battle.fightAt > 0 && now >= battle.fightAt) {
      battle.phase = 'fighting';
      emit(battle, { type: 'phase', phase: 'fighting' });
    }
    // Nothing is counted while the picture fades in.
    const count =
      battle.fightAt === 0
        ? null
        : introCount(battle.fightAt - now, battle.phase === 'fighting' ? now - battle.fightAt : -1);
    if (count !== battle.countdown) {
      battle.countdown = count;
      if (count !== null && count > 0) emit(battle, { type: 'count', n: count });
      battle.hudTimer = 0;
      publish(battle);
    }
    // The persistent VR control can switch modes during a fight without quitting it.
    if (inputInXr !== inXr()) {
      inputInXr = inXr();
      disposeInputs(battle);
      const canvas = engine.getRenderingCanvas();
      const human = battle.controller.human;
      if (inputInXr && xr) {
        // One headset, one person: the first side only.
        if (human) battle.inputs[human] = createXrFighterInput(xr);
      } else if (canvas) battle.inputs = createFlatInputs(battle, canvas);
      if (!inputInXr) scene.activeCamera = arenaCam.camera;
    }
    for (const input of Object.values(battle.inputs)) input?.update(dt);
    const { controller } = battle;
    const humanInputs: Partial<Record<FighterId, () => FighterInput>> = {};
    for (const side of controller.humans) {
      const input = battle.inputs[side];
      if (!input) continue;
      humanInputs[side] = () => {
        const me = fighterOf(controller.state, side);
        const enemy = fighterOf(controller.state, opponentOf(controller.state, side));
        const projectileInFlight = controller.state.projectiles.some(
          (projectile) => projectile.owner === side,
        );
        const intent = input.next({
          steerableProjectile: me.steerableProjectile === true,
          projectileInFlight,
        });
        if (intent.forfeit) {
          controller.forfeit(side);
          settle(battle);
        }
        return intentToInput(intent, me, enemy);
      };
    }
    const events: BattleEvent[] =
      battle.phase === 'fighting' || battle.phase === 'over'
        ? controller.advance(dt, humanInputs)
        : [];
    // Most events change what the panels show at once; the fire's bite every step does not, and
    // the panels' own beat is often enough for the health it takes.
    let telling = false;
    for (const event of events) {
      const burn = event.type === 'hit' && event.source === 'hazard';
      telling ||= !burn;
      if (burn) battle.burningUntil[event.target] = now + BURN_SHOWN_MS;
      else if (
        event.type === 'hit' &&
        event.source !== 'cost' &&
        controller.humans.includes(event.target)
      )
        battle.hitFlash = HIT_FLASH_SECONDS;
      if (event.type === 'timeout') battle.timedOut = true;
      if (event.type === 'death' && battle.killedAt === 0) battle.killedAt = now;
      if (event.type === 'hit' && event.source === 'blow') feelBlow(battle, event);
      sparks.react(event, controller.state);
      emit(battle, { type: 'battle', event });
    }
    battle.hitFlash = Math.max(0, battle.hitFlash - dt);
    if (battle.phase === 'fighting') reportMotion(battle, dt);

    // The kill plays in slow motion: the clips (through the scene's clock), the bodies, the sparks
    // and the camera. The simulation is over by then, and the sound keeps its time.
    const pace = battle.killedAt > 0 ? killPace((now - battle.killedAt) / 1000) : 1;
    scene.animationTimeScale = pace;
    sparks.pace = pace;
    const shownDt = dt * pace;
    for (const id of FIGHTER_IDS) {
      battle.views[id]?.update(
        controller.pose(id),
        fighterOf(controller.state, id),
        events,
        shownDt,
      );
    }
    // Each ring lies where its fighter is drawn, which a hit-stop holds back a moment.
    readyRings.update(
      controller.state.seats.flatMap((id) => {
        const view = battle.views[id];
        if (!view) return [];
        const fighter = fighterOf(controller.state, id);
        return [
          {
            fighter,
            pos: view.root.position,
            yaw: view.root.rotation.y,
            color: battle.colors[id] ?? Color3.White(),
          },
        ];
      }),
      shownDt,
    );

    // In the headset the player looks at a model on their table with their own eyes; on a screen
    // the fixed camera frames the arena at true size. Neither follows a fighter's head.
    const asDiorama = inXr();
    placeArena(asDiorama);
    const living = controller.state.seats
      .map((id) => fighterOf(controller.state, id))
      .filter((f) => f.alive)
      .map((f) => ({ x: f.pos.x, z: f.pos.z }));
    if (!asDiorama) arenaCam.update(living, shownDt);
    lamps.update(living);
    fog.update(shownDt);
    liquids.update(shownDt);
    const hudWasShown = hud.shown;
    placeHud(asDiorama);
    // Put on the headset mid-duel: the plaque is drawn into at once, not on the next beat.
    if (hud.shown && !hudWasShown) battle.hudTimer = 0;

    const shots = controller.projectiles();
    if (shots.length === 0) projectiles.isVisible = false;
    else {
      // Written in place: a fresh buffer every frame a shot flies made the GPU buffer anew each
      // time (client-arena:quality#12).
      if (shots.length > shotRoom) {
        shotRoom = Math.max(8, shots.length * 2);
        shotMatrices = new Float32Array(shotRoom * 16);
        shotColors = new Float32Array(shotRoom * 4);
        projectiles.thinInstanceSetBuffer('matrix', shotMatrices, 16, false);
        projectiles.thinInstanceSetBuffer('color', shotColors, 4, false);
      }
      shots.forEach((p, i) => {
        // Drawn in its owner's flavour, and along where it flies.
        const live = controller.state.projectiles.find((q) => q.id === p.id);
        const look = SHOT_LOOKS[(live && battle.flavours[live.owner]) ?? 'bolt'];
        shotMatrix(p.pos, live?.vel, look, shotAt).copyToArray(shotMatrices, i * 16);
        shotColors[i * 4] = look.color[0];
        shotColors[i * 4 + 1] = look.color[1];
        shotColors[i * 4 + 2] = look.color[2];
        shotColors[i * 4 + 3] = 1;
      });
      projectiles.thinInstanceCount = shots.length;
      projectiles.thinInstanceBufferUpdated('matrix');
      projectiles.thinInstanceBufferUpdated('color');
      projectiles.isVisible = true;
    }

    const { state } = controller;
    if (state.suddenDeath) {
      ring.setEnabled(true);
      // The ring closes on the fight, not on the middle of the map, so it follows the state.
      ring.position.set(state.safeCentre.x, RING_HEIGHT / 2, state.safeCentre.z);
      ring.scaling.set(state.safeRadius, 1, state.safeRadius);
    }

    battle.hudTimer -= dt;
    if (battle.hudTimer <= 0 || telling) {
      battle.hudTimer = 1 / HUD_HZ;
      if (hud.shown) updateHud(battle);
      if (battle.phase === 'fighting' || battle.phase === 'over') publish(battle);
    }

    settle(battle);
    if (battle.phase === 'over' && performance.now() >= battle.overAt) void finish(battle);
  };

  /**
   * A blow felt in the hands of a person in the fight: a thump for one taken, harder for a harder
   * blow, and a tick for one landed (client-input:quality#3). The ground's burn and a shot's cost
   * are not blows, and nothing is felt by a device without a motor.
   */
  const feelBlow = (battle: RunningBattle, hit: Extract<BattleEvent, { type: 'hit' }>) => {
    const target = fighterOf(battle.controller.state, hit.target);
    const share = hit.damage / Math.max(1, target.maxHp);
    battle.inputs[hit.target]?.pulse?.(Math.min(1, 0.4 + share * 3), 0.12);
    if (hit.by !== hit.target) battle.inputs[hit.by]?.pulse?.(0.25, 0.04);
  };

  /** Moves a decided fight into its outcome phase. */
  const settle = (battle: RunningBattle) => {
    if (battle.phase !== 'fighting' || !battle.controller.result) return;
    battle.phase = 'over';
    battle.overAt =
      performance.now() + (battle.setup.lockstep ? SERVED_OUTCOME_SECONDS : OUTCOME_SECONDS) * 1000;
    if (document.pointerLockElement) document.exitPointerLock();
    publish(battle);
    emit(battle, { type: 'phase', phase: 'over' });
  };

  /** Lights the arena as its square is on the board: noon on a light one, moonlight on a dark. */
  const light = (daylight: ArenaDaylight) => {
    sun.intensity = daylight.sunIntensity;
    sun.diffuse.copyFrom(daylight.sunColor);
    sun.direction.copyFrom(daylight.sunDirection);
    skyLight.intensity = daylight.skyIntensity;
    skyLight.diffuse.copyFrom(daylight.skyColor);
    skyLight.groundColor.copyFrom(daylight.groundColor);
  };
  let daylight = arenaDaylight(1);

  const switchIn = (battle: RunningBattle) => {
    battle.shown = true;
    root.setEnabled(true);
    const pending = battle.setup.state.pendingBattle;
    daylight = arenaDaylight(
      pending ? cellBrightness(battle.setup.game, battle.setup.state, pending.x, pending.y) : 1,
    );
    light(daylight);
    skyLight.setEnabled(true);
    sun.setEnabled(true);
    lamps.setNight(1 - daylight.brightness);
    fog.setLight(daylight.brightness);
    lamps.setEnabled(true);
    ring.setEnabled(false);
    emit(battle, { type: 'ambience', file: ambienceFile(arena.theme?.ambience, daylight.night) });
    const human = battle.controller.human;
    const asDiorama = Boolean(xr && inXr());
    inputInXr = asDiorama;
    // Placed first, so the scene knows as it switches its world whether the room stays round it.
    placeArena(asDiorama);
    deps.onModeChange(true, battle.setup);
    const living = battle.controller.state.seats
      .map((id) => fighterOf(battle.controller.state, id))
      .map((f) => ({ x: f.pos.x, z: f.pos.z }));
    arenaCam.reset(living);

    if (asDiorama && xr) {
      // The arena is a model on the table the player is already sitting at, so the headset's own
      // camera stays exactly where the board left it: nothing to parent, nothing to move.
      xr.teleportation?.detach();
      // Keep pointing active for the world-fixed radio and VR controls. The plate answers only
      // while a grip is held (`createImmersiveControls`), since the trigger attacks here.
      if (human) battle.inputs[human] = createXrFighterInput(xr);
    } else {
      scene.activeCamera = arenaCam.camera;
      const canvas = engine.getRenderingCanvas();
      if (canvas) battle.inputs = createFlatInputs(battle, canvas);
    }
    // Watched in the headset too, where the whole canvas is the frame: a player who takes the
    // headset off mid-duel gets the arena framed clear of the panel (client-arena:quality#7).
    watchFrame();
    placeHud(asDiorama);
    frameObserver = scene.onBeforeRenderObservable.add(frame);
  };

  const switchOut = (battle: RunningBattle) => {
    if (frameObserver) scene.onBeforeRenderObservable.remove(frameObserver);
    frameObserver = null;
    unwatchFrame();
    disposeInputs(battle);
    hud.hide();
    if (xr) {
      if (inXr()) {
        xr.teleportation?.attach();
        xr.pointerSelection?.attach();
      }
    }
    for (const view of Object.values(battle.views)) view?.dispose();
    battle.views = {};
    projectiles.isVisible = false;
    scene.animationTimeScale = 1;
    sparks.pace = 1;
    sparks.clear();
    readyRings.clear();
    ring.setEnabled(false);
    root.setEnabled(false);
    skyLight.setEnabled(false);
    sun.setEnabled(false);
    lamps.setEnabled(false);
    daylight = arenaDaylight(1);
    emit(battle, { type: 'ambience', file: null, leaving: true });
    deps.onModeChange(false, battle.setup);
  };

  const finish = async (battle: RunningBattle) => {
    if (battle.phase === 'leaving') return;
    battle.phase = 'leaving';
    await deps.fade(0, FADE_SECONDS);
    if (disposed) return;
    switchOut(battle);
    running = null;
    feed.publish(null);
    const result = battle.controller.result ?? { winnerId: null, winnerDamage: 0 };
    battle.setup.onFinished(result);
    await deps.fade(1, FADE_SECONDS);
  };

  const onMesh = (mesh: AbstractMesh) => {
    shadows.addShadowCaster(mesh);
  };

  return {
    get active() {
      return running !== null;
    },
    get onTable() {
      return running !== null && placedAsDiorama === true;
    },
    centre: () => toWorld({ x: arena.width / 2, y: 0, z: arena.height / 2 }),
    get daylight() {
      return daylight;
    },
    theme: arena.theme,
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get aiSkill() {
      return aiSkill;
    },
    setAiSkill(skill) {
      aiSkill = { accuracy: skill.accuracy, speed: skill.speed };
    },
    start(setup) {
      if (running || disposed) return;
      const ctx = createBattleContext(setup.game, arena, tileset);
      const state = setup.lockstep
        ? createServedBattle(ctx, setup.state, { seed: setup.seed, levels: setup.levels })
        : createBattle(ctx, setup.state, setup.seed);
      if (!setup.lockstep)
        for (const side of state.seats) {
          if (setup.humans.includes(side)) continue;
          const fighter = state.fighters[side]!;
          // A game's seats carry an authored skill; a staged duel's (the tests') may lie between two.
          const skill = setup.skills?.[side] ?? aiSkill;
          applyDuelLevelStats(
            fighter,
            setup.game.roster[fightsAs(fighter)]!,
            nearestDuelLevel(skill),
          );
        }
      const controller = new BattleController({
        ctx,
        state,
        humans: setup.humans,
        humanFriendlyAi: setup.humanFriendlyAi,
        seed: setup.seed,
        skill: aiSkill,
        skills: setup.skills,
        lockstep: setup.lockstep,
      });
      // What the square's light gives each side, which the intro names (x-aaa-gap#3).
      const pending = setup.state.pendingBattle;
      const light: Partial<Record<FighterId, number>> = {};
      for (const id of state.seats) {
        const piece = pending && pieceById(setup.state, fighterOf(state, id).pieceId);
        if (piece)
          light[id] = luminosityHitPoints(setup.game, setup.state, piece, pending.x, pending.y);
      }
      const battle: RunningBattle = {
        setup,
        controller,
        views: {},
        inputs: {},
        phase: 'intro',
        shown: false,
        fightAt: 0,
        countdown: null,
        light,
        overAt: 0,
        killedAt: 0,
        timedOut: false,
        burningUntil: {},
        flavours: {},
        colors: {},
        hudTimer: 0,
        hitFlash: 0,
        stride: {},
        second: 0,
      };
      for (const id of state.seats) {
        const f = fighterOf(state, id);
        const enemy = fighterOf(state, opponentOf(state, id));
        battle.flavours[id] = shotFlavour(roster[f.character], roster[enemy.character]);
        battle.colors[id] = Color3.FromHexString(sideColor(setup, f.faction));
      }
      // Every fighter is drawn now. Nobody is inside a head any more, and seeing your own
      // character at its true size — a dragon big, a goblin small — is most of why the camera
      // moved outside in the first place (D-16).
      // A death's dust and the motes of its fade are the duel's sparks (x-aaa-gap#8).
      const effects = {
        dust: (from: Vec3, to: Vec3) => sparks.dust(from, to),
        motes: (from: Vec3, to: Vec3, color: string) => sparks.motes(from, to, color),
      };
      for (const id of state.seats) {
        battle.views[id] = new FighterView({
          scene,
          models,
          roster,
          fighter: fighterOf(state, id),
          parent: root,
          color: sideColor(setup, fighterOf(state, id).faction),
          onMesh,
          effects,
        });
      }
      sparks.prepare(
        state,
        state.seats.map((id) => sideColor(setup, fighterOf(state, id).faction)),
      );
      running = battle;
      publish(battle);
      void (async () => {
        await deps.fade(0, FADE_SECONDS);
        if (disposed || running !== battle) return;
        switchIn(battle);
        await deps.fade(1, FADE_SECONDS);
        if (disposed || running !== battle) return;
        // The fight begins in the frame loop once the hold is over.
        battle.fightAt = performance.now() + INTRO_HOLD_SECONDS * 1000;
      })();
    },
    forfeit(side) {
      if (!running) return;
      running.controller.forfeit(side);
      settle(running);
    },
    abort() {
      const battle = running;
      if (!battle) return;
      // It used to play on to its end over the board that had taken its place, and hid it.
      battle.phase = 'leaving';
      running = null;
      feed.publish(null);
      if (battle.shown) switchOut(battle);
      else {
        for (const view of Object.values(battle.views)) view?.dispose();
        battle.views = {};
        sparks.clear();
      }
      void deps.fade(1, FADE_SECONDS);
    },
    continueOutcome() {
      if (running?.phase === 'over') running.overAt = Math.min(running.overAt, performance.now());
    },
    frame() {
      return arenaCam.frame();
    },
    skipClockTo(seconds) {
      if (!running || !running.setup.practice || running.setup.lockstep) return;
      const { state } = running.controller;
      state.time = Math.max(state.time, seconds);
    },
    setShadows(quality) {
      for (const chunk of chunks) chunk.receiveShadows = quality !== 'off';
      if (trees) trees.mesh.receiveShadows = quality !== 'off';
      applyShadowQuality(shadows, quality);
    },
    setPalette(next) {
      palette = next;
    },
    dispose() {
      disposed = true;
      listeners.clear();
      if (running) switchOut(running);
      running = null;
      hud.dispose();
      arenaCam.dispose();
      shadows.dispose();
      sun.dispose();
      skyLight.dispose();
      for (const chunk of chunks) chunk.dispose();
      dressing.dispose();
      // Materials and the atlas go with their meshes: a game with several arenas builds a stage
      // per arena, and each used to leave them in the scene (client-arena:bugs#6).
      tileMaterial.dispose(false, true);
      projectiles.dispose(false, true);
      sparks.dispose();
      readyRings.dispose();
      ring.dispose(false, true);
      plinth.dispose(false, true);
      root.dispose();
    },
  };
}
